import { memo, useState } from "react";
import Demo from "@coworker/apprestructured/src/task-management/demo/pages/ComponentDemoPage/demos/Demo/Demo";
import Name from "@coworker/apprestructured/src/task-management/demo/pages/ComponentDemoPage/demos/Name/Name";
import Highlight from "./Highlight/Highlight";
import RefillQuantity from "@coworker/apprestructured/src/task-management/components/RefillQuantity/RefillQuantity";
import Actions from "./Actions/Actions";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import FixaCheckbox from "@coworker/apprestructured/src/shared/wrappers/FixaCheckbox/FixaCheckbox";
import { RefillUnitType } from "@coworker/apprestructured/src/task-management/constants/RefillUnitType";
import { MAXIMUM_QUANTITY } from "@coworker/apprestructured/src/task-management/services/useRefillQuantity";

const sanitizeNumber = (value: number) => {
  return /^[0-9]*$/.test(String(value)) ? Math.max(value, 0) : 0;
};

const makeNumericOnChange = (setFn: (value: number) => void) => {
  return (value: number) => {
    setFn(sanitizeNumber(value));
  };
};

function RefillQuantityDemo() {
  const [available, setAvailable] = useState(174);
  const [pieces, setPieces] = useState(57);
  const [multipacks, setMultipacks] = useState(9);
  const [multipackValue, setMultipackValue] = useState(25);
  const [pallets, setPallets] = useState(2);
  const [palletValue, setPalletValue] = useState(100);
  const [maxPieces, setMaxPieces] = useState(217);
  const [activeUnit, setActiveUnit] = useState(RefillUnitType.PIECES);
  const [showValidation, setShowValidation] = useState(false);

  const panelConfigs = {
    [RefillUnitType.PIECES]: {
      label: "Pieces",
      panelProps: {
        type: RefillUnitType.PIECES,
        value: pieces,
        onChange: makeNumericOnChange(setPieces),
        suffixLabel: "Pieces",
        max: maxPieces,
        current: pieces,
        isError: pieces > maxPieces,
        isWarning: pieces > available,
        errorMsg: "Exceeding max pieces",
        warningMsg: "Exceeding available stock",
        isThresholdWarning: pieces > MAXIMUM_QUANTITY,
      },
    },
    [RefillUnitType.MULTIPACK]: {
      label: "Multipack",
      panelProps: {
        type: RefillUnitType.MULTIPACK,
        value: multipacks,
        onChange: makeNumericOnChange(setMultipacks),
        infoText: `1 Multipack = ${multipackValue} pieces`,
        suffixLabel: "Multipacks",
        max: maxPieces,
        current: multipacks * multipackValue,
        isError: multipacks * multipackValue > maxPieces,
        isWarning: multipacks * multipackValue > available,
        errorMsg: "Exceeding max pieces",
        warningMsg: "Exceeding available stock",
        isThresholdWarning: multipacks * multipackValue > MAXIMUM_QUANTITY,
      },
    },
    [RefillUnitType.PALLET]: {
      label: "Pallet",
      panelProps: {
        type: RefillUnitType.PALLET,
        value: pallets,
        onChange: makeNumericOnChange(setPallets),
        infoText: `1 Pallet = ${palletValue} pieces`,
        suffixLabel: "Pallets",
        max: maxPieces,
        current: pallets * palletValue,
        isError: pallets * palletValue > maxPieces,
        isWarning: pallets * palletValue > available,
        errorMsg: "Exceeding max pieces",
        warningMsg: "Exceeding available stock",
        isThresholdWarning: pallets * palletValue > MAXIMUM_QUANTITY,
      },
    },
  };

  return (
    <Demo>
      <Name>RefillQuantity</Name>
      <Highlight>
        <RefillQuantity
          activeUnit={activeUnit}
          onUnitChange={setActiveUnit}
          showValidation={showValidation}
          panelConfigs={panelConfigs}
        />
      </Highlight>
      <Actions>
        <FixaCheckbox
          label="showValidation"
          value="refillquantitydemo-showvalidation"
          id="refillquantitydemo-showvalidation"
          checked={showValidation}
          onChange={() => setShowValidation((prev) => !prev)}
        />
        <FixaInputField
          label="available"
          type="number"
          id="refillquantitydemo-available"
          value={String(available)}
          onChange={(e) => setAvailable(Number(e.target.value))}
        />
        <FixaInputField
          label="maxPieces"
          type="number"
          id="refillquantitydemo-maxpieces"
          value={String(maxPieces)}
          onChange={(e) => setMaxPieces(Number(e.target.value))}
        />
        <FixaInputField
          label="multipackValue"
          type="number"
          id="refillquantitydemo-multipackvalue"
          value={String(multipackValue)}
          onChange={(e) => setMultipackValue(Number(e.target.value))}
        />
        <FixaInputField
          label="palletValue"
          type="number"
          id="refillquantitydemo-palletvalue"
          value={String(palletValue)}
          onChange={(e) => setPalletValue(Number(e.target.value))}
        />
      </Actions>
    </Demo>
  );
}

export default memo(RefillQuantityDemo);
